import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { MotifVerticalNavigationSubmenu } from './vertical-navigation-submenu';

import { useVerticalNavigationContext } from '../vertical-navigation-context';

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {string} [className]
 */
/**
 * @param {Props} props
 */
const MotifVerticalNavigationMenu = ({ className = '', children }) => {
  const { collapse } = useVerticalNavigationContext();
  const [activeIndex, setActiveIndex] = React.useState(null);
  const [pristine, setPristine] = React.useState(true);
  const [hoverIndex, setHoverIndex] = React.useState(null);

  React.useEffect(() => {
    if (collapse) {
      setActiveIndex(null);
    }
  }, [collapse]);

  const classes = classNames({
    'motif-vertical-navigation-menu': true,
    [className]: true
  });

  const mappedChildren = React.useMemo(() => React.Children.map(children, (child, index) => {
    if (child.type !== MotifVerticalNavigationSubmenu) {
      return child;
    }

    const isOverSubmenu = index === hoverIndex;
    const isActive = index === activeIndex;
    const open = !collapse && pristine ? Boolean(child.props.selected) : isActive;

    return React.cloneElement(child, {
      onClick: () => {
        if (pristine) {
          setPristine(false);
        }
        setActiveIndex(open ? null : index);
        if (child.props.onClick) {
          child.props.onClick();
        }
      },
      onMouseEnter: () => {
        setHoverIndex(index);
        if (collapse) {
          setActiveIndex(open ? null : index);
        }
      },
      onMouseLeave: () => {
        setHoverIndex(null);
        if (collapse) {
          setActiveIndex(null);
        }
      },
      onOutsideClick: () => {
        if (isActive) {
          setActiveIndex(null);
        }
      },
      open: collapse ? isOverSubmenu || open : open,
      children: React.Children.map(child.props.children, (nestedChild) => React.cloneElement(nestedChild, { child: true }))
    });
  }), [children, activeIndex, pristine, collapse, hoverIndex]);

  return (
    <nav role="menubar" className={classes} data-motif-vertical-navigation-menu>
      {mappedChildren}
    </nav>
  );
};

MotifVerticalNavigationMenu.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired
};

export { MotifVerticalNavigationMenu };
